/**
 * Which host serves which path, now that the signed-in app has its own host
 * (ADR-0062): praenum.com keeps the marketing pages and /funding, and
 * app.praenum.com serves everything a client signs in to, plus the pages that
 * emails link to.
 *
 * Path names are identical on both hosts, so moving between them is a host swap
 * with no mapping table. nginx enforces the same split with 301s
 * (infrastructure/nginx/sites-enabled/grantsignal.conf); this is the client-side
 * half, for the navigation nginx never sees: a react-router <Link> renders the
 * next page without a request, so without this a "Sign in" link on a marketing
 * page would render /signin on the marketing origin and the session cookie would
 * land there.
 *
 * Pure functions with no browser or Vite globals, so the tests run this file
 * under node (backend/tests/test_two_hosts.py). The runtime wiring is
 * src/twoHost.js.
 */

export const SITE_PATHS = {
  exact: ['/', '/pricing', '/about', '/faq', '/privacy', '/terms', '/accessibility', '/funding'],
  prefixes: ['/funding/'],
}

export const APP_PATHS = {
  exact: [
    '/signin', '/signup', '/forgot-password', '/reset-password', '/check-inbox',
    '/confirm-result', '/unsubscribe', '/feedback', '/dashboard', '/plans',
    '/grants', '/cultivate', '/preferences', '/account', '/admin',
  ],
  prefixes: ['/grants/', '/funders/', '/billing/'],
}

// Where sign-in sends someone who did not ask for anywhere in particular.
export const DEFAULT_RETURN = '/dashboard'

// Pages that are part of signing in or up. Returning to one after sign-in would
// loop, so a return path that names one is replaced by the default.
const AUTH_PAGES = new Set([
  '/signin', '/signup', '/forgot-password', '/reset-password', '/check-inbox',
  '/confirm-result',
])

function matches(paths, pathname) {
  return paths.exact.includes(pathname) || paths.prefixes.some(p => pathname.startsWith(p))
}

/** 'site', 'app', or null for a path neither host claims. */
export function hostFor(pathname) {
  if (matches(SITE_PATHS, pathname)) return 'site'
  if (matches(APP_PATHS, pathname)) return 'app'
  return null
}

/**
 * Which of the two hosts this page is on, or null when it is on neither: local
 * dev, where one host serves everything and nothing here may redirect.
 */
export function roleOf(origin, origins) {
  if (origin === origins.site) return 'site'
  if (origin === origins.app) return 'app'
  return null
}

/**
 * The URL to replace this page with because it is on the wrong host, or null.
 *
 * `/` on the app host is the one path that is not sent across: it shows the
 * dashboard (owner decision, #210), and a signed-out visitor then lands on
 * /signin.
 *
 * The result is a fixed origin followed by the path as given, never
 * `new URL(path, base)`: a path of `//evil.com/x` resolves off-origin through
 * the URL constructor, and nginx serves the SPA for exactly that path. Such a
 * path is claimed by neither host anyway, so it is never sent across.
 */
export function hostRedirect({ pathname, search = '', hash = '' }, here, origins) {
  if (!here) return null
  const owner = hostFor(pathname)
  if (!owner || owner === here) return null
  if (here === 'app' && pathname === '/') return null
  return origins[owner] + pathname + search + hash
}

/**
 * The href for a link to `path` from a page on `here`: the path itself when it
 * stays on this host, an absolute URL when it crosses. `path` is always a
 * literal we wrote (backend/tests/test_navigation_targets.py), so a malformed
 * one is a programming error and throws rather than producing a link.
 */
export function hrefFor(path, here, origins) {
  if (typeof path !== 'string' || !/^\/(?![/\\])/.test(path)) {
    throw new Error(`Link target must be one leading '/', got ${JSON.stringify(path)}`)
  }
  if (!here) return path
  const owner = hostFor(path.split(/[?#]/)[0])
  if (!owner || owner === here) return path
  return origins[owner] + path
}

/**
 * The page to go to after signing in, from the destination RequireAuth recorded.
 *
 * The value travels in router state, not in the URL, so no link can set it. It
 * is checked as though it could: one leading `/` followed by a path character
 * (never `//host` or `/\host`, the two shapes that leave the origin), no
 * backslash or whitespace anywhere, and an app page that is not part of signing
 * in. Anything else gets the dashboard.
 */
export function safeReturnPath(value) {
  if (typeof value !== 'string' || value.length > 2048) return DEFAULT_RETURN
  if (!/^\/[A-Za-z0-9]/.test(value) || /[\\\s]/.test(value)) return DEFAULT_RETURN
  const pathname = value.split(/[?#]/)[0]
  if (hostFor(pathname) !== 'app' || AUTH_PAGES.has(pathname)) return DEFAULT_RETURN
  return value
}
